import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { languageNameFor, useUiLanguage } from '../language';
import { cn } from '@/lib/utils';
import { MONO_LABEL_TYPE } from '@/components/editorial';
import { parseTranslationMeta } from '@/lib/translation-utils';

interface TrferBubbleProps {
  content: string;
  authorName: string;
  /** Raw metadata field from the message — may be an object or JSON string. */
  metadata?: Record<string, unknown> | string;
  /** Recipient's preferred language; drives which translation surfaces. */
  viewerLanguage?: string;
}


export function TrferBubble({ content, authorName, metadata, viewerLanguage = 'en' }: TrferBubbleProps) {
  const { t } = useTranslation('chat');
  const uiLanguage = useUiLanguage();
  const [showOriginal, setShowOriginal] = useState(false);
  const initial = authorName.trim().charAt(0).toUpperCase() || 'T';

  const meta = parseTranslationMeta(metadata);
  const originalLang = meta?.original_language;
  const translated = meta?.translations?.[viewerLanguage as keyof typeof meta.translations];
  const isTranslated = !!(meta && originalLang && originalLang !== viewerLanguage && translated);
  const hasError = !!meta?.translation_error;

  const displayText = isTranslated ? translated! : content;

  return (
    <div className="flex gap-2 mb-2">
      <div className="w-8 h-8 rounded-full bg-status-warn-solid text-status-warn-solid-fg flex items-center justify-center text-body font-bold shrink-0">
        {initial}
      </div>
      <div className="flex-1 max-w-[75%]">
        <div className={cn(MONO_LABEL_TYPE, 'text-status-warn-fg mb-1')}>
          {t('handoff.trferBubble.author', { name: authorName })}
        </div>
        <div className="bg-stevens-surface text-stevens-ink border-2 border-status-warn-fg rounded-2xl rounded-tl-sm p-3 text-body whitespace-pre-wrap break-words">
          {showOriginal && isTranslated ? content : displayText}
        </div>
        {isTranslated && (
          <button
            type="button"
            className="text-caption italic text-stevens-ink-3 mt-1 hover:underline"
            onClick={() => setShowOriginal(s => !s)}
          >
            {showOriginal
              ? t('handoff.trferBubble.showingOriginal', { language: languageNameFor(originalLang, uiLanguage) })
              : t('handoff.trferBubble.translatedFrom', { language: languageNameFor(originalLang, uiLanguage) })}
          </button>
        )}
        {meta && !isTranslated && hasError && (
          <div className="text-caption italic text-status-warn-fg mt-1">
            {t('handoff.translation.unavailable')}
          </div>
        )}
      </div>
    </div>
  );
}
